/**
 * PageHeader — DeCarbonIQ Design System
 * Consistent page-level header for all workspace sub-pages.
 *
 * Props:
 *   label      — small uppercase namespace label (e.g. "SME WORKSPACE")
 *   title      — primary page title
 *   subtitle   — optional secondary line
 *   backUrl    — if set, renders a back-nav link
 *   backLabel  — text for back nav (default "Back")
 *   onBack     — optional click handler (overrides backUrl)
 *   actions    — optional JSX rendered on the right side
 *   className  — extra classes
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export function PageHeader({
  label,
  title,
  subtitle,
  backUrl,
  backLabel = "Back",
  onBack,
  actions,
  className = "",
}) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) { onBack(); return; }
    if (backUrl) navigate(backUrl);
  };

  return (
    <div className={`mb-6 ${className}`} data-testid="page-header">
      {(backUrl || onBack) && (
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-4 transition-colors"
          data-testid="page-header-back"
        >
          <ArrowLeft size={13} />
          {backLabel}
        </button>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          {label && (
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">
              {label}
            </p>
          )}
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h1>
          {subtitle && (
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0 pt-1">{actions}</div>
        )}
      </div>
    </div>
  );
}

export default PageHeader;
